import { BooLogo } from "@/components/BooLogo";
import { AuthMethodDivider, GoogleSignInButton, MicrosoftSignInButton } from "@/components/SocialSignInButton";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { useResendCooldown } from "@/hooks/useResendCooldown";
import { authClient } from "@/lib/auth-client";
import { LOCKED_EMAIL_COPY, lockedEmailReason } from "@/lib/identity-link";
import { showDevSignInToast } from "@/lib/dev-toast";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export function LoginPage() {
  useDocumentTitle("Log in");
  const navigate = useNavigate();
  const { next, email: prefillEmail } = useSearch({ strict: false }) as {
    next?: string;
    email?: string;
  };

  // Identity-bound entry links (an invitation, or the waitlist-approval
  // "Finish setup" email) pass ?email=. The address is locked to the link's
  // target so the user cannot accidentally sign in with the wrong account.
  const lockReason = lockedEmailReason(next, prefillEmail);
  const isLockedEmail = lockReason !== null;

  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState(prefillEmail ?? "");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const devToastIdRef = useRef<string | number | undefined>(undefined);
  const lastSentEmailRef = useRef("");
  const { secondsLeft, canResend, startCooldown } = useResendCooldown();

  async function sendCode() {
    setError("");
    setLoading(true);
    try {
      const { error: sendError } = await authClient.emailOtp.sendVerificationOtp({
        email,
        type: "sign-in",
      });
      if (sendError) throw new Error(sendError.message ?? "Failed to send code");
      lastSentEmailRef.current = email;
      startCooldown();
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setError(
        msg.includes("Failed to fetch") || msg.includes("NetworkError")
          ? "Unable to reach the server. Please check your connection and try again."
          : msg
      );
      return false;
    } finally {
      setLoading(false);
    }
  }

  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault();
    // During the cooldown the code already sent to this email is still
    // valid — going back and resubmitting must not bypass the resend timer.
    if (!canResend && email === lastSentEmailRef.current) {
      setError("");
      setStep("code");
      return;
    }
    const ok = await sendCode();
    if (ok) {
      setStep("code");
      devToastIdRef.current = showDevSignInToast({
        email,
        onSignedIn: () => navigate({ to: next || "/" }),
      });
    }
  }

  async function handleResendCode() {
    if (!canResend || loading) return;
    await sendCode();
  }

  async function verifyCode(code: string) {
    setError("");
    setLoading(true);
    try {
      const { error: signInError } = await authClient.signIn.emailOtp({ email, otp: code });
      if (signInError) throw new Error(signInError.message ?? "Invalid code");
      if (devToastIdRef.current !== undefined) toast.dismiss(devToastIdRef.current);
      // New users (auto-created via signin) land here without a name.
      // The onboarding-index route detects empty name → shows the welcome
      // step before the org form. Existing users skip it.
      await navigate({ to: next || "/" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign in failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    await verifyCode(otp);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex w-full max-w-2xl border rounded-xl bg-card p-3">
        {/* Left — brand hero */}
        <div
          className="login-grain relative hidden md:flex flex-col items-center justify-center basis-1/2 shrink-0 p-8 text-gray-800 aspect-[1/1.4] rounded-xl overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #ececee 0%, #e4dced 45%, #dce4f0 100%)",
          }}
        >
          <div className="relative z-10 flex flex-col justify-between h-full w-full">
            <div className="flex items-center gap-2">
              <BooLogo className="size-12 shrink-0 rounded-lg" />
              <span className="text-2xl font-semibold tracking-tight">Boo</span>
            </div>
            <p className="text-2xl font-light leading-snug text-left text-gray-700">
              Enterprise AI agents that work alongside your team.
            </p>
          </div>
        </div>

        {/* Right — sign-in form */}
        <div className="basis-1/2 flex flex-col px-6 pt-5 pb-2">
          <div className="flex-1 flex flex-col justify-center space-y-6">
            {step === "email" ? (
              <>
                <div className="space-y-1">
                  <h1 className="text-2xl font-semibold">Sign in</h1>
                  <p className="text-sm text-muted-foreground">
                    We'll email you a sign-in code.
                  </p>
                </div>

                <div className="space-y-2">
                  <GoogleSignInButton
                    next={next}
                    loginHint={isLockedEmail ? prefillEmail : undefined}
                    onError={setError}
                  />
                  <MicrosoftSignInButton
                    next={next}
                    loginHint={isLockedEmail ? prefillEmail : undefined}
                    onError={setError}
                  />
                </div>

                <AuthMethodDivider />

                <form onSubmit={handleSendCode} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      readOnly={isLockedEmail}
                      className={isLockedEmail ? "bg-muted cursor-not-allowed" : ""}
                      required
                      autoFocus={!isLockedEmail}
                    />
                    {lockReason && (
                      <p className="text-xs text-muted-foreground">{LOCKED_EMAIL_COPY[lockReason]}</p>
                    )}
                  </div>
                  {error && <InlineError error={error} />}
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Sending…" : "Send code"}
                  </Button>
                </form>

                <p className="text-sm text-center text-muted-foreground">
                  No account?{" "}
                  <Link to="/signup" className="underline underline-offset-4 hover:text-foreground">
                    Sign up
                  </Link>
                </p>
              </>
            ) : (
              <>
                <div className="space-y-1">
                  <h1 className="text-2xl font-semibold">We emailed you the code.</h1>
                  {email && (
                    <p className="text-xs text-muted-foreground">
                      Sent to <span className="text-foreground">{email}</span>.
                      {!isLockedEmail && (
                        <>
                          {" "}Wrong email?{" "}
                          <button
                            type="button"
                            onClick={() => {
                              setStep("email");
                              setOtp("");
                              setError("");
                            }}
                            className="underline underline-offset-2 hover:text-foreground"
                          >
                            Change
                          </button>
                        </>
                      )}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Don't see it? Check your spam folder.
                  </p>
                </div>

                <form onSubmit={handleVerify} className="space-y-4">
                  <InputOTP
                    id="otp"
                    maxLength={6}
                    value={otp}
                    onChange={setOtp}
                    onComplete={(value) => {
                      setOtp(value);
                      if (!loading) verifyCode(value);
                    }}
                    autoFocus
                    containerClassName="w-full"
                    className="w-full"
                  >
                    <InputOTPGroup className="flex-1">
                      <InputOTPSlot index={0} className="flex-1 !h-11" />
                      <InputOTPSlot index={1} className="flex-1 !h-11" />
                      <InputOTPSlot index={2} className="flex-1 !h-11" />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup className="flex-1">
                      <InputOTPSlot index={3} className="flex-1 !h-11" />
                      <InputOTPSlot index={4} className="flex-1 !h-11" />
                      <InputOTPSlot index={5} className="flex-1 !h-11" />
                    </InputOTPGroup>
                  </InputOTP>
                  {error && <InlineError error={error} />}
                  <Button type="submit" className="w-full" disabled={loading || otp.length !== 6}>
                    {loading ? "Verifying…" : "Sign in"}
                  </Button>
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={!canResend || loading}
                    className="w-full text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                  >
                    {canResend ? "Resend code" : `Resend in ${secondsLeft}s`}
                  </button>
                  {/* Announce availability once instead of every countdown tick. */}
                  <span aria-live="polite" className="sr-only">
                    {canResend ? "You can now resend the code." : ""}
                  </span>
                </form>
              </>
            )}
          </div>

          <p className="text-[8px] text-right text-muted-foreground pt-2">
            <Link to="/privacy-policy" className="underline underline-offset-4 hover:text-foreground">
              Privacy Policy
            </Link>
            {" · "}
            <Link to="/terms-and-conditions" className="underline underline-offset-4 hover:text-foreground">
              Terms
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
